import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { HelpCircle } from "lucide-react";
import { FormCard } from "@/components/FormCard";
import { Button, Combobox, Label, Switch, SimpleSelect, Tooltip } from "@/components/ui";
import {
  ContentPageTheme,
  Form,
  ReviewSortType,
  ReviewWidgetConfig,
  ReviewWidgetConfigServices,
} from "@/services/bedrock";

interface OptionType {
  label: string;
  value: string;
}

type EditableConfig = Pick<
  ReviewWidgetConfig,
  | "showStarSummary"
  | "showSentimentSummary"
  | "showNpsSummary"
  | "formId"
  | "contentPageThemeId"
  | "reviewSortType"
  | "useCondensedQuotes"
  | "approvedForSocialOnly"
  | "autoScrollEnabled"
>;

const getEditableConfig = (config: ReviewWidgetConfig): EditableConfig => ({
  showStarSummary: config.showStarSummary,
  showSentimentSummary: config.showSentimentSummary,
  showNpsSummary: config.showNpsSummary,
  formId: config.formId ?? null,
  contentPageThemeId: config.contentPageThemeId ?? null,
  reviewSortType: config.reviewSortType,
  useCondensedQuotes: config.useCondensedQuotes,
  approvedForSocialOnly: config.approvedForSocialOnly,
  autoScrollEnabled: config.autoScrollEnabled,
});

const SummarySettingRow: React.FC<{
  id: string;
  label: string;
  tooltip: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ id, label, tooltip, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3">
    <div className="flex items-center gap-1.5 min-w-0">
      <Label htmlFor={id}>{label}</Label>
      <Tooltip>
        <Tooltip.Trigger
          render={
            <button
              type="button"
              className="inline-flex items-center text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
              aria-label={`About ${label.toLowerCase()}`}
            >
              <HelpCircle className="size-3.5" />
            </button>
          }
        />
        <Tooltip.Content className="max-w-64">{tooltip}</Tooltip.Content>
      </Tooltip>
    </div>
    <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

interface Props {
  config: ReviewWidgetConfig;
  updateConfig: (updates: Partial<ReviewWidgetConfig>) => void;
  themes: ContentPageTheme[];
  forms: Form[];
}

const ReviewWidgetConfigForm: React.FC<Props> = ({ config, updateConfig, themes, forms }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [savedConfig, setSavedConfig] = useState(() => getEditableConfig(config));

  const hasChanges = useMemo(
    () => JSON.stringify(getEditableConfig(config)) !== JSON.stringify(savedConfig),
    [config, savedConfig]
  );

  const themeSelectItems = useMemo<OptionType[]>(
    () =>
      themes.map(theme => ({
        label: theme.name,
        value: String(theme.id),
      })),
    [themes]
  );

  const formSelectItems = useMemo<OptionType[]>(
    () =>
      forms.map(form => ({
        label: form.displayName,
        value: String(form.id),
      })),
    [forms]
  );

  const handleCancel = () => {
    if (!hasChanges) return;
    updateConfig(savedConfig);
  };

  const handleSave = () => {
    if (!hasChanges) return;
    setIsSaving(true);
    const payload = getEditableConfig(config);
    ReviewWidgetConfigServices.updateConfig(config.id, payload)
      .then(updated => {
        updateConfig(updated);
        setSavedConfig(getEditableConfig(updated));
        toast.success("Review widget settings saved");
      })
      .catch(error => {
        console.error(error);
        toast.error("Error saving review widget settings");
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <FormCard>
      <FormCard.Header
        title="Configurations"
        description="Configure and preview the embeddable review widget for Player websites."
      />

      <FormCard.Content>
        <div className="grid gap-4 @min-[35rem]:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Summaries</Label>
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              <SummarySettingRow
                id="show-star-summary"
                label="Star summary"
                tooltip="Hidden on the widget if the Player's average star rating is below 4.0."
                checked={config.showStarSummary}
                onCheckedChange={checked => updateConfig({ showStarSummary: checked })}
              />
              <SummarySettingRow
                id="show-sentiment-summary"
                label="Sentiment summary"
                tooltip="Hidden on the widget if fewer than 70% of reviews are positive."
                checked={config.showSentimentSummary}
                onCheckedChange={checked => updateConfig({ showSentimentSummary: checked })}
              />
              <SummarySettingRow
                id="show-nps-summary"
                label="NPS summary"
                tooltip="Hidden on the widget if the Player's NPS is below 50."
                checked={config.showNpsSummary}
                onCheckedChange={checked => updateConfig({ showNpsSummary: checked })}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reviews</Label>
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <Label htmlFor="use-condensed-quotes">Condensed quotes</Label>
                <Switch
                  id="use-condensed-quotes"
                  checked={config.useCondensedQuotes}
                  onCheckedChange={checked => updateConfig({ useCondensedQuotes: checked })}
                />
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <Label htmlFor="approved-for-social-only">Approved for social only</Label>
                <Switch
                  id="approved-for-social-only"
                  checked={config.approvedForSocialOnly}
                  onCheckedChange={checked => updateConfig({ approvedForSocialOnly: checked })}
                />
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <Label htmlFor="auto-scroll-enabled">Auto scroll</Label>
                <Switch
                  id="auto-scroll-enabled"
                  checked={config.autoScrollEnabled}
                  onCheckedChange={checked => updateConfig({ autoScrollEnabled: checked })}
                />
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <Label>Sort</Label>
                <SimpleSelect
                  value={config.reviewSortType}
                  onValueChange={value => updateConfig({ reviewSortType: (value || "featured") as ReviewSortType })}
                  items={[
                    { label: "Featured", value: "featured" },
                    { label: "Latest", value: "latest" },
                  ]}
                  align="end"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 @min-[35rem]:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label>Theme</Label>
            <Combobox
              items={themeSelectItems}
              value={
                config.contentPageThemeId
                  ? themeSelectItems.find(item => item.value === String(config.contentPageThemeId)) || null
                  : null
              }
              onValueChange={(val: OptionType | null) =>
                updateConfig({ contentPageThemeId: val ? Number(val.value) : null })
              }
              searchable
              showClear
              placeholder="Use Player default"
              emptyText="No themes found"
              isItemEqualToValue={(current, selected) => current?.value === selected?.value}
              itemToStringLabel={item => item?.label || ""}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Review form</Label>
            <Combobox
              items={formSelectItems}
              value={config.formId ? formSelectItems.find(item => item.value === String(config.formId)) || null : null}
              onValueChange={(val: OptionType | null) => updateConfig({ formId: val ? Number(val.value) : null })}
              searchable
              showClear
              placeholder="Use Player default"
              emptyText="No forms found"
              isItemEqualToValue={(current, selected) => current?.value === selected?.value}
              itemToStringLabel={item => item?.label || ""}
            />
          </div>
        </div>
      </FormCard.Content>

      <FormCard.Actions>
        <Button
          type="button"
          variant="outline"
          className="w-20"
          disabled={!hasChanges || isSaving}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button type="button" loading={isSaving} className="w-20" disabled={!hasChanges} onClick={handleSave}>
          Save
        </Button>
      </FormCard.Actions>
    </FormCard>
  );
};

export { ReviewWidgetConfigForm };
